'use client'
import React from 'react'
import { CalendarDays, UserRound } from 'lucide-react'
import type { Post } from '@/payload-types'
import { Container } from '@/components/Container'
import { getBackgroundConfig } from '@/components/Container/utils'
import { Column } from '@/components/Column/column'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import { formatDateTime } from '@/utilities/formatDateTime'
import { formatAuthors } from '@/utilities/formatAuthors'

const columnHasImage = (column: any): boolean => Boolean(column?.image?.media)

// Figma "Case Study Post" hero: 40px bold navy title over 16px navy-2 copy.
const TEXT_COLUMN_CLASSES = {
  heading: 'text-[32px] md:text-[40px] leading-[1.2] font-bold text-navy',
  richText: 'text-[16px] leading-[1.6] text-navy-2 [&_p]:mb-3 [&_p:last-child]:mb-0',
}

export const PostHero: React.FC<{
  post: Post
}> = ({ post }) => {
  const { populatedAuthors, publishedAt, lastUpdated, hero } = post
  // A refreshed post shows its refresh date; a new post shows its publish date.
  const displayDate = lastUpdated || publishedAt
  const dateLabel = lastUpdated ? 'Last updated on' : 'Published on'
  const { post: postHero } = hero || {}
  const { layout = 'fullWidth', columns = [], bg, showPostMeta = true } = postHero || {}

  const authorNames = populatedAuthors?.length ? formatAuthors(populatedAuthors) : ''
  const firstAuthor = populatedAuthors?.[0]
  const avatar =
    firstAuthor?.avatar && typeof firstAuthor.avatar === 'object' ? firstAuthor.avatar : null

  const backgroundConfig = getBackgroundConfig({
    bg: bg ? { layers: bg.layers?.map((layer: any) => ({ ...layer }) as any) || [] } : undefined,
    defaultColor: 'transparent',
  })

  // Author row: 60px photo (or a placeholder icon), name, and the date with a
  // calendar icon. Injected under the first text column.
  const metadataElement = showPostMeta && (authorNames || displayDate) && (
    <div className="flex items-center justify-center gap-3 text-center md:justify-start md:text-left">
      {authorNames && (
        <div className="flex size-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
          {avatar ? (
            <Media
              resource={avatar}
              className="size-full"
              imgClassName="size-full rounded-full object-cover"
            />
          ) : (
            <UserRound className="size-7 text-navy-2" aria-hidden="true" />
          )}
        </div>
      )}
      <div className="flex flex-col gap-1 text-navy">
        {authorNames && (
          <p className="m-0 text-[20px] font-bold leading-[1.2]">by {authorNames},</p>
        )}
        {displayDate && (
          <p className="m-0 flex items-center gap-2 text-[16px] leading-[1.6]">
            <CalendarDays className="size-4 shrink-0 text-navy-2" aria-hidden="true" />
            <span>
              {dateLabel} <time dateTime={displayDate}>{formatDateTime(displayDate)}</time>
            </span>
          </p>
        )}
      </div>
    </div>
  )

  const imageIndex = (columns || []).findIndex(columnHasImage)
  const firstTextIndex = (columns || []).findIndex((column: any) => !columnHasImage(column))
  const imageOnLeft = imageIndex === 0 && (columns || []).length > 1

  return (
    <Container background={backgroundConfig} className="py-10">
      {layout === 'fullWidth' ? (
        // Full width layout: only first column displayed
        <div className="w-full">
          {columns && columns[0] && (
            <>
              {/* @ts-ignore */}
              <Column size={'full'} {...columns[0]} mediaPriority />
              {metadataElement && <div className="my-6">{metadataElement}</div>}
            </>
          )}
        </div>
      ) : (
        // Two column layout: copy on one side, image on a rounded colour panel
        // that bleeds to the viewport edge on the other (Figma 2967:2816).
        <div
          className={cn(
            'grid grid-cols-1 items-center gap-y-10 lg:gap-x-10',
            imageOnLeft ? 'lg:grid-cols-[5fr_4fr]' : 'lg:grid-cols-[4fr_5fr]',
          )}
        >
          {(columns || []).map((column: any, index: number) => {
            if (columnHasImage(column)) {
              return (
                <div
                  key={index}
                  className="relative order-first flex items-center py-6 lg:order-none lg:min-h-[800px] lg:py-0"
                >
                  <div
                    aria-hidden="true"
                    className={cn(
                      'absolute inset-y-0 hidden bg-blue-3 lg:block',
                      imageOnLeft
                        ? '-left-[50vw] right-[30%] rounded-r-[80px]'
                        : 'left-[30%] -right-[50vw] rounded-l-[80px]',
                    )}
                  />
                  <div
                    className={cn('relative z-10 w-full max-w-[560px]', imageOnLeft && 'ml-auto')}
                  >
                    {/*// @ts-ignore */}
                    <Column
                      size={'half'}
                      {...column}
                      mediaPriority
                      classNames={{ image: 'rounded-[20px]' }}
                    />
                  </div>
                </div>
              )
            }

            return (
              <div key={index} className="flex w-full flex-col gap-5 lg:py-10">
                {/*// @ts-ignore */}
                <Column size={'half'} {...column} classNames={TEXT_COLUMN_CLASSES} />
                {index === firstTextIndex && metadataElement}
              </div>
            )
          })}
        </div>
      )}
    </Container>
  )
}
